/**
 * What a position can lose TODAY — ROADMAP 6.12.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE PROBLEM WITH "UNLIMITED"
 *
 * A naked short call's loss at expiry is unbounded, so every honest max-loss
 * calculation answers "unlimited". That answer is correct and useless. A
 * trader who sees it every day for every short learns nothing from it, cannot
 * compare two positions by it, and cannot tell the day the number genuinely
 * got worse. A risk display that says the same thing whatever happens is not
 * a risk display.
 *
 * It is also answering a question nobody asked. "Unlimited" is a statement
 * about spot at infinity. Nobody holds a position to infinity; they hold it
 * to the close, or to expiry, through a move of a size the market itself has
 * an opinion about.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO NUMBERS INSTEAD OF ONE WORD
 *
 *   1. THE BAND.   What is the worst this loses if spot moves by `move`
 *                  points in either direction? Always finite, always
 *                  comparable between positions, and it changes as the
 *                  market changes — which is the whole point.
 *
 *   2. THE RATE.   When the loss keeps growing past the band, say how FAST:
 *                  rupees per point, and from where. "Unlimited" becomes
 *                  "₹4,875 for every 100 points above 23,400".
 *
 * The second is the part that makes a naked short readable. An asymptote is
 * not something a human can act on; a speed is. A trader who knows the rate
 * knows what a 300-point gap costs before it happens, and can decide whether
 * a stop at 23,400 is worth its premium — which is the decision they were
 * actually trying to make when they looked at the number.
 *
 *          loss │                              ╲  ← rate: ₹/point
 *               │  ┌──── band ────┐             ╲
 *               │  │   worst ●    │              ╲
 *            ───┼──┴──────────────┴───────────────╲──── spot
 *                 −move    spot   +move      from here on, uncapped
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE BAND IS EXACT RATHER THAN SAMPLED
 *
 * A payoff is piecewise linear with kinks only at strikes, so its extremes
 * inside any interval occur at a kink or at an endpoint — never between them.
 * Evaluating those points gives the exact worst case with no sample count to
 * tune. `basketPayoff.ts` makes the same argument for breakevens and for the
 * same reason: a sampled curve can miss a spike entirely, and on an Iron Fly
 * the spike IS the position.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS IS NOT
 *
 * NOT a margin number. SPAN is the exchange's own worst case over its own
 * scenarios and it is the authority on what is blocked; this is about what is
 * at stake, which is a different question with a different answer.
 *
 * NOT a probability. The band says "if spot moves this far", not "spot will".
 * Deciding how far is the caller's job — see `RiskBand` — and whether that
 * came from implied volatility or from a flat percentage is something the UI
 * must say out loud, because the two deserve different amounts of trust.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { kinksOf, payoffAt, payoffShape } from './basketPayoff.js';
/**
 * The risk of one set of legs over a band of spot.
 *
 * `legs` are UNITS and signed, exactly as `PayoffLeg` means them, so a basket
 * and a lone leg go through the same path — "the risk of a group" and "the
 * risk of everything" differ only in what is passed in.
 */
export function openRisk(legs, band, 
/**
 * What the basket is worth at a hypothetical spot, TODAY.
 *
 * ── WHY THIS IS AN ARGUMENT AND NOT A SETTING ─────────────────────────
 *
 * Left out, the basket is valued at EXPIRY — intrinsic only, no model and
 * no inputs. That is exact and needs nothing, but it is optimistic in the
 * short run: a short option still carrying time value loses MORE on a
 * 400-point move today than the same move at expiry would cost.
 *
 * Passed in, the caller supplies a real pricer (`payoffMatrix.valueAt`,
 * which is Black-76 with the time remaining) and the answer becomes about
 * this afternoon rather than about settlement day. The pricer is injected
 * rather than imported so this module keeps needing no volatility, no
 * clock and no rate of its own.
 *
 * IT CHANGES HOW THE BAND IS SEARCHED. At expiry the payoff is piecewise
 * linear, so its extremes live exactly at the kinks and the edges. With
 * time value the curve is smooth and a worst case can sit between two
 * strikes, so a custom pricer is SAMPLED across the band as well.
 */
valueAtSpot) {
    if (legs.length === 0)
        return { kind: 'FLAT' };
    if (!Number.isFinite(band.spot) || band.spot <= 0) {
        return { kind: 'UNKNOWN', why: 'no live spot for this underlying' };
    }
    if (!Number.isFinite(band.move) || band.move <= 0) {
        return { kind: 'UNKNOWN', why: 'no move size to judge the risk over' };
    }
    const lo = Math.max(0, band.spot - band.move);
    const hi = band.spot + band.move;
    /*
     * Endpoints, plus every kink INSIDE the band. The extremes of a piecewise
     * linear function on an interval live at exactly these points, so this is
     * the exact answer rather than a fine-grained guess.
     */
    const at = [lo, hi, ...kinksOf(legs).filter((k) => k > lo && k < hi)];
    /*
     * A smooth curve can hide its worst case between two strikes, so a priced
     * basket is swept as well. 48 steps over a band this narrow puts the grid
     * inside a point on an index — finer than the tick the loss is quoted in.
     */
    if (valueAtSpot) {
        for (let i = 1; i < SWEEP; i += 1)
            at.push(lo + ((hi - lo) * i) / SWEEP);
    }
    const value_ = valueAtSpot ?? ((spot) => payoffAt(legs, spot));
    let worst = { value: value_(at[0]), atSpot: at[0] };
    let best = worst;
    for (const spot of at) {
        const value = value_(spot);
        if (value < worst.value)
            worst = { value, atSpot: spot };
        if (value > best.value)
            best = { value, atSpot: spot };
    }
    if (!Number.isFinite(worst.value) || !Number.isFinite(best.value)) {
        return { kind: 'UNKNOWN', why: 'a leg is missing the strike or entry price needed to price it' };
    }
    const shape = payoffShape(legs);
    const kinks = kinksOf(legs);
    /*
     * WHICH SIDE RUNS AWAY, and from where.
     *
     * A negative slope above means every further point of spot costs money,
     * for ever. The rate applies from the LAST kink — past it the payoff is a
     * straight line and the slope is the whole story — or from the band edge
     * when the band already reaches beyond every strike.
     *
     * Only one side is reported, and it is the worse one. Both tails losing at
     * once is possible (a long butterfly sold against nothing), but a trader
     * reading one line needs the number that kills them, not two numbers to
     * compare under pressure.
     */
    const above = shape.slopeAbove < 0
        ? {
            side: 'ABOVE',
            fromSpot: Math.max(hi, kinks.length > 0 ? kinks[kinks.length - 1] : hi),
            perPoint: Math.abs(shape.slopeAbove),
            bounded: false,
        }
        : null;
    const below = shape.slopeBelow > 0
        ? {
            side: 'BELOW',
            fromSpot: Math.min(lo, kinks.length > 0 ? kinks[0] : lo),
            perPoint: Math.abs(shape.slopeBelow),
            // Spot has a floor at zero, so this one does end — a long way down.
            bounded: true,
        }
        : null;
    const beyond = above && below ? (above.perPoint >= below.perPoint ? above : below) : (above ?? below);
    return {
        kind: 'RISK',
        worst: round2(worst.value),
        atSpot: worst.atSpot,
        best: round2(best.value),
        beyond,
    };
}
/**
 * The WORST this position can do, at expiry, over every spot — ROADMAP 6.13.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A RISK NUMBER THAT MOVES EVERY TICK IS NOT A RISK NUMBER
 *
 * `openRisk` above answers "what could this lose today, if spot moves one
 * session's worth". Everything in that sentence is live: the band is
 * `spot x IV / sqrt(252)`, the valuation is Black-76 against solved IVs, and
 * the interval slides with spot. So the figure changed on every tick — the
 * drawer showed "-1.94 L if +/-2,194 pts" and both halves of it danced.
 *
 * Reported 2026-11-01: that is the wrong question. The one a trader actually
 * asks of this line is "how much is at stake in this position", and the
 * answer to THAT must not move unless the position does. A number that
 * fluctuates cannot be remembered between glances, cannot be compared
 * against yesterday, and cannot be the thing a size decision is made from.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY EXPIRY MAKES IT STAND STILL
 *
 * At expiry an option is worth its intrinsic value: `max(0, spot - strike)`.
 * No model, no volatility, no clock — pricing models exist to value
 * UNCERTAINTY and at expiry there is none. The payoff is therefore piecewise
 * linear with kinks only at strikes, and its extremes over the whole domain
 * live at a kink, at zero, or in a tail.
 *
 * Every input is then FROZEN DATA: strikes, signed quantities and the prices
 * already paid. Spot is not an input. Implied volatility is not an input.
 * The number changes when the trader changes the position and at no other
 * time, which is exactly the property that was missing.
 *
 *   Iron Condor      a constant. The width less the credit, and it is the
 *                    number the structure was put on for.
 *   long option      a constant: the premium paid. It cannot lose more.
 *   short put        large but FINITE — spot has a floor at zero, so this
 *                    one ends, a long way down.
 *   short call       genuinely unbounded, and this says so rather than
 *                    quoting a figure from a band nobody chose.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IS GIVEN UP, STATED
 *
 * Time value. A short option still carrying premium loses MORE on a move
 * today than the same move would cost at settlement, so this is optimistic
 * about this afternoon. That is the deliberate trade: `openRisk` is the
 * honest answer to "today" and is unreadable because it never settles;
 * this is the honest answer to "at stake" and holds still. The tail rate on
 * an unbounded position is the same either way, because a slope does not
 * care about time value.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function maxRisk(legs) {
    if (legs.length === 0)
        return { kind: 'FLAT' };
    const shape = payoffShape(legs);
    const kinks = kinksOf(legs);
    /*
     * Zero is always a candidate: the left tail is BOUNDED because spot cannot
     * go under it, and a short put's settlement against a worthless underlying
     * is frequently the real max loss of a basket.
     */
    const candidates = [0, ...kinks];
    let worst = { value: payoffAt(legs, candidates[0]), atSpot: candidates[0] };
    let best = worst;
    for (const spot of candidates) {
        const value = payoffAt(legs, spot);
        if (value < worst.value)
            worst = { value, atSpot: spot };
        if (value > best.value)
            best = { value, atSpot: spot };
    }
    if (!Number.isFinite(worst.value) || !Number.isFinite(best.value)) {
        return { kind: 'UNKNOWN', why: 'a leg is missing the strike or entry price needed to price it' };
    }
    /*
     * ONLY THE UPPER TAIL CAN RUN AWAY. A falling spot stops at zero and
     * `candidates` already evaluated it, so a loss in that direction is
     * finite and is in `worst` — reporting it as a rate as well would say
     * "uncapped" about a position that is capped.
     */
    const beyond = shape.slopeAbove < 0
        ? {
            side: 'ABOVE',
            // The LAST strike: past it the payoff is a straight line and the
            // slope is the whole story.
            fromSpot: kinks.length > 0 ? kinks[kinks.length - 1] : 0,
            perPoint: Math.abs(shape.slopeAbove),
            bounded: false,
        }
        : null;
    return { kind: 'RISK', worst: round2(worst.value), atSpot: worst.atSpot, best: round2(best.value), beyond };
}
/**
 * The same, for a whole book — pooled, never summed.
 *
 * Summing each group's worst case adds losses that happen at DIFFERENT
 * spots, describing a day that cannot occur. Pooling lets a long call in one
 * basket offset a short call in another, which is the only reason a total
 * above a list of groups is worth showing at all.
 *
 * Legs from different underlyings must not be pooled; the caller groups by
 * underlying first.
 */
export function portfolioMaxRisk(groups) {
    return maxRisk(groups.flat());
}
/**
 * Add up the risk of several groups.
 *
 * ── NOT A SUM OF WORST CASES ───────────────────────────────────────────────
 *
 * Summing each group's own worst case would add losses that happen at
 * DIFFERENT spots, and the total would describe a day that cannot occur —
 * one where NIFTY finished at 23,000 for one basket and 24,000 for another.
 * On a book of offsetting positions that overstates the risk badly enough to
 * be worse than no number at all.
 *
 * So the legs are pooled and evaluated ONCE against one band. A long call in
 * one basket then correctly offsets a short call in another, which is the
 * whole reason a portfolio figure is worth showing above a list of groups
 * that each already show their own.
 *
 * Legs from DIFFERENT underlyings cannot share a band and must not be pooled;
 * the caller groups by underlying before getting here.
 */
export function portfolioRisk(groups, band, valueAtSpot) {
    return openRisk(groups.flat(), band, valueAtSpot);
}
/** Steps across the band when a custom pricer makes the curve smooth. */
const SWEEP = 48;
const round2 = (n) => Math.round(n * 100) / 100;
//# sourceMappingURL=openRisk.js.map